import Select from "@mui/material/Select";
import React, { FunctionComponent, useState } from "react";
import { Theme } from "@emotion/react";
import { styled } from "@iventis/styles";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { MenuClasses, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { StyledItemSelect, StyledMenuItem } from "./selectors/select-atoms";

export interface DropDownOption {
    name: string;
    onSelection: (id?: string) => void;
    disabled?: boolean;
    id?: string;
    colour?: string;
    renderOption?: (value: DropDownOption) => React.ReactElement;
    dataCy?: string;
}

interface DropDownProps {
    optionGroup: DropDownOption[][];
    selectedId: string;
    icon?: IconProp;
    disabled?: boolean;
    className?: string;
    renderOption?: (value: DropDownOption) => React.ReactElement;
    dataCy?: string;
    menuClasses?: Partial<MenuClasses>;
}

/**
 * Component which shows options in a drop down format
 * @param {DropDownOption[][]} optionGroup - nested array of group options, each nested array will have their elements show as options, each array will be seperated by a divder
 * @param {string} selectedId - id of the selected option from optionGroup nested arrays
 * @param {IconProp} icon - icon be shown alongside a drop down option if they have a colour property (optional)
 */
export const NestedDropDownComponent: FunctionComponent<DropDownProps> = ({
    optionGroup,
    selectedId,
    disabled,
    className,
    renderOption = (value) => <IconNameComponent name={value.name} colour={value.id != null ? value.colour : null} selected={value.id === selectedId} checkIcon={false} />,
    dataCy,
    menuClasses,
}) => {
    const theme = useTheme<Theme>();
    const [open, setOpen] = useState<boolean>(false);
    return (
        <Select
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            className={className}
            data-cy={dataCy}
            disabled={disabled}
            input={<StyledItemSelect $verticalPadding="8px" $borderColour={theme.shades.three} />}
            value={selectedId}
            MenuProps={{
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "left",
                },
                classes: menuClasses,
            }}
            renderValue={() => {
                const selectedOption = optionGroup.flat().find((option) => option.id === selectedId);
                return renderOption(selectedOption);
            }}
        >
            {optionGroup.map((options, optionsIndex) =>
                options.map((option, index) => (
                    <StyledMenuItem
                        data-cy={option.dataCy} // for testing purposes
                        divider={index === options.length - 1 && optionsIndex !== optionGroup.length - 1}
                        onClick={() => {
                            if (option.disabled !== true) {
                                option.onSelection(option.id);
                                setOpen(false);
                            }
                        }}
                        style={{ backgroundColor: theme.typographyColors.blank }}
                        value={option.id}
                        key={option.id}
                        selected={selectedId === option.id}
                        disabled={option.disabled === true}
                    >
                        {option.renderOption ? option.renderOption(option) : renderOption(option)}
                    </StyledMenuItem>
                ))
            )}
        </Select>
    );
};

interface IconNameProps {
    name: string;
    selected: boolean;
    checkIcon: boolean;
    colour?: string;
}

/**
 * A component which creates a icon and name pairing
 * @param {string} name - name to be displayed
 * @param {boolean} selected - if selected
 * @param {boolean} checkIcon - if to display a tick next to the selected schedule
 * @param {string} - colour of the dot displayed with the name
 */
const IconNameComponent: FunctionComponent<IconNameProps> = ({ name, selected, checkIcon, colour }) => (
    <StyledContainer enableFlex={checkIcon}>
        {checkIcon && <CheckIconContainer>{selected && <FontAwesomeIcon icon={["fas", "check"]} />}</CheckIconContainer>}
        <MenuItemContent>
            {colour && <FontAwesomeIcon style={{ marginRight: "10px" }} size="sm" icon={["fas", "circle"]} color={colour} />}
            <MenuItemText>{name}</MenuItemText>
        </MenuItemContent>
    </StyledContainer>
);
const MenuItemText = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledContainer = styled(MenuItemText)<{ enableFlex: boolean }>`
    display: ${({ enableFlex }) => `${enableFlex ? "flex" : "block"}`};
`;

const MenuItemContent = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CheckIconContainer = styled.div`
    width: 20px;
    margin-right: 10px;
`;
